import React, {useState, useEffect} from 'react';
import {useInput, InputProps, useDataProvider} from 'react-admin';
import {Button, Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, IconButton} from '@mui/material';
import {PhotoLibrary, Clear, CloudUpload} from '@mui/icons-material';
import {FileManager} from '../file-manager';
import {MediaFile} from '../../types/index';

interface MediaInputProps extends Omit<InputProps, 'source'> {
    source: string;
    label?: string;
    helperText?: string;
    multiple?: boolean;
    accept?: string;
    showPreview?: boolean;
    hasDeleteMany?: boolean;
    resource?: string;
}

export const MediaInput: React.FC<MediaInputProps> = ({
                                                          source,
                                                          label = 'Медиа-файл',
                                                          helperText,
                                                          multiple = false,
                                                          accept,
                                                          showPreview = true,
                                                          hasDeleteMany = false,
                                                          resource = 'media',
                                                          ...rest
                                                      }) => {
    const {
        field: {value, onChange},
        fieldState: {error},
    } = useInput({source, ...rest});

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<MediaFile[]>([]);
    const [mediaData, setMediaData] = useState<MediaFile[]>([]);
    const [loading, setLoading] = useState(false);

    const dataProvider = useDataProvider();

    // Load media data when value changes
    useEffect(() => {
        const loadMediaData = async () => {
            if (!value) {
                setMediaData([]);
                return;
            }

            setLoading(true);
            try {
                const ids = multiple ? (Array.isArray(value) ? value : [value]) : [value];
                const mediaPromises = ids.map(id =>
                    dataProvider.getOne(resource, {id})
                );

                const mediaResults = await Promise.all(mediaPromises);
                const loadedMedia = mediaResults.map(result => result.data);
                setMediaData(loadedMedia);
            } catch (error) {
                console.error('Ошибка загрузки медиа-файлов:', error);
                setMediaData([]);
            } finally {
                setLoading(false);
            }
        };

        loadMediaData();
    }, [value, multiple, dataProvider]);

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedFiles([]);
    };

    const handleFileSelect = (files: MediaFile[]) => {
        setSelectedFiles(files);
    };

    const handleConfirmSelection = () => {
        if (selectedFiles.length > 0) {
            if (multiple) {
                // For multiple selection, store array of Media IDs
                const ids = selectedFiles.map(file => file.id);
                onChange(ids);
            } else {
                // For single selection, store just the Media ID
                onChange(selectedFiles[0].id);
            }
        }
        handleCloseModal();
    };

    const handleClear = () => {
        onChange(multiple ? [] : '');
    };

    const renderPreview = () => {
        if (!showPreview || !value || loading) return null;

        if (mediaData.length === 0) return null;

        return (
            <Box sx={{mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1}}>
                {mediaData.map((media: MediaFile, index: number) => (
                    <Box
                        key={media.id}
                        sx={{
                            position: 'relative',
                            width: 100,
                            height: 100,
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {media.mimeType?.startsWith('image/') ? (
                            <img
                                src={media.url}
                                alt={media.filename || 'Preview'}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                        ) : (
                            <Typography variant="caption" sx={{p: 1, textAlign: 'center'}}>
                                {media.filename?.split('.').pop()?.toUpperCase() || 'Файл'}
                            </Typography>
                        )}
                        <IconButton
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                bgcolor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                '&:hover': {bgcolor: 'rgba(0,0,0,0.7)'},
                            }}
                            onClick={() => {
                                if (multiple && Array.isArray(value)) {
                                    const newIds = value.filter((_, i) => i !== index);
                                    onChange(newIds);
                                } else {
                                    handleClear();
                                }
                            }}
                        >
                            <Clear fontSize="small"/>
                        </IconButton>
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <Box>
            <Typography variant="body2" sx={{mb: 1, fontWeight: 500}}>
                {label}
            </Typography>

            <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                <Button
                    variant="outlined"
                    startIcon={<PhotoLibrary/>}
                    onClick={handleOpenModal}
                    sx={{minWidth: 150}}
                >
                    {value ? 'Изменить медиа' : 'Выбрать медиа'}
                </Button>

                {value && (
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Clear/>}
                        onClick={handleClear}
                        size="small"
                    >
                        Очистить
                    </Button>
                )}
            </Box>

            {helperText && (
                <Typography variant="caption" color="textSecondary" sx={{mt: 0.5, display: 'block'}}>
                    {helperText}
                </Typography>
            )}

            {error && (
                <Typography variant="caption" color="error" sx={{mt: 0.5, display: 'block'}}>
                    {error.message}
                </Typography>
            )}

            {renderPreview()}

            {/* Media Selection Modal */}
            <Dialog
                open={isModalOpen}
                onClose={handleCloseModal}
                maxWidth="lg"
                fullWidth
                slotProps={
                    {
                        paper: {
                            sx: {height: '80vh'}
                        }
                    }
                }
            >
                <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <CloudUpload/>
                    Выбрать медиа-файл{multiple ? 'ы' : ''}
                </DialogTitle>

                <DialogContent sx={{p: 0, height: '100%'}}>
                    <Box sx={{height: '100%', p: 2}}>
                        <FileManager
                            config={{
                                multiple,
                                showPreview: true,
                                showMetadata: true,
                                enableDragDrop: true,
                                selectionMode: true, // Enable selection mode
                                accept,
                            }}
                            onFileSelect={handleFileSelect}
                            hasDeleteMany={hasDeleteMany}
                        />
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseModal}>
                        Отмена
                    </Button>
                    <Button
                        onClick={handleConfirmSelection}
                        variant="contained"
                        disabled={selectedFiles.length === 0}
                    >
                        Выбрать {selectedFiles.length > 0 && `(${selectedFiles.length})`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
